import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { ChevronDown, Download } from 'lucide-react';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { formatMoneda } from '../state/format';
import { formatDiferencia, formatFechaHora, formatFechaSinHora, formatVariacion } from '../state/formatVersiones';
import { VersionCentro } from '../types';
import { VersionCambios } from './VersionCambios';

const texto = { fontFamily: 'Poppins, sans-serif' };

interface VersionCardProps {
  version: VersionCentro;
  versionAnterior: number | null;
  onDescargar: (version: number) => Promise<void>;
}

function destinatarios(enviadoA: string): string {
  const correos = enviadoA.split(',').map((c) => c.trim()).filter(Boolean);
  return correos.length > 1 ? `${correos[0]} +${correos.length - 1}` : correos[0] ?? '';
}

function resumen(version: VersionCentro, versionAnterior: number | null): string | null {
  if (!version.cambios) return null;
  const { diferencia, variacion_pct } = version.cambios.total;
  const pct = variacion_pct !== null ? ` (${formatVariacion(variacion_pct)})` : '';
  return `${formatDiferencia(diferencia)}${pct} frente a la v${versionAnterior}`;
}

// SPEC-206 (UC-007, UC-008, UC-010, design §7b) — tarjeta de una versión enviada, legible sin
// expandir; "Ver cambios" despliega la comparación con la anterior (acordeón accesible).
export const VersionCard: React.FC<VersionCardProps> = ({ version, versionAnterior, onDescargar }) => {
  const [expandida, setExpandida] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const anteriorALaSpec = !version.pdf_disponible;
  const idPanel = `cambios-v${version.version}`;
  const lineaResumen = resumen(version, versionAnterior);

  const descargar = async () => {
    setDescargando(true);
    try {
      await onDescargar(version.version);
    } finally {
      setDescargando(false);
    }
  };

  return (
    <Box
      data-testid={`version-card-${version.version}`}
      sx={{ backgroundColor: '#fff', border: `1px solid ${TOKENS.hair}`, borderRadius: '12px', padding: '12px 14px', marginBottom: '10px' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography sx={{ ...texto, fontSize: fs(14), fontWeight: 700, color: TOKENS.ink }}>v{version.version}</Typography>
          {version.es_vigente && (
            <Box component="span" sx={{ ...texto, fontSize: fs(10), fontWeight: 700, letterSpacing: '1px', color: '#fff', backgroundColor: TOKENS.purple, borderRadius: '999px', padding: '1px 8px' }}>
              VIGENTE
            </Box>
          )}
        </Box>
        <Typography sx={{ ...texto, fontSize: fs(13), fontWeight: 700, color: TOKENS.ink }}>{formatMoneda(version.total)}</Typography>
      </Box>
      {version.enviada_en && (
        <Typography sx={{ ...texto, fontSize: fs(11.5), color: TOKENS.muted }}>{formatFechaHora(version.enviada_en)}</Typography>
      )}
      <Typography sx={{ ...texto, fontSize: fs(11.5), color: TOKENS.text }}>
        A {destinatarios(version.enviado_a)}{version.validez ? ` · válida al ${formatFechaSinHora(version.validez)}` : ''}
      </Typography>
      {anteriorALaSpec && (
        <Typography sx={{ ...texto, fontSize: fs(11.5), color: TOKENS.muted, marginTop: '4px' }}>
          Enviada antes del historial detallado — PDF y comparación no disponibles
        </Typography>
      )}
      {!anteriorALaSpec && !version.cambios && (
        <Typography sx={{ ...texto, fontSize: fs(11.5), color: TOKENS.muted }}>Primera versión</Typography>
      )}
      {lineaResumen && <Typography sx={{ ...texto, fontSize: fs(11.5), fontWeight: 600, color: TOKENS.ink }}>{lineaResumen}</Typography>}

      {!anteriorALaSpec && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '6px', marginTop: '8px' }}>
          <Button
            size="small"
            onClick={descargar}
            disabled={descargando}
            startIcon={descargando ? <CircularProgress size={12} /> : <Download size={14} aria-hidden />}
            sx={{ ...texto, textTransform: 'none', fontSize: fs(12), color: TOKENS.purple }}
          >
            Descargar PDF
          </Button>
          {version.cambios && (
            <Button
              size="small"
              aria-expanded={expandida}
              aria-controls={idPanel}
              onClick={() => setExpandida((v) => !v)}
              endIcon={<ChevronDown size={14} aria-hidden style={{ transform: expandida ? 'rotate(180deg)' : undefined }} />}
              sx={{ ...texto, textTransform: 'none', fontSize: fs(12), color: TOKENS.text }}
            >
              Ver cambios
            </Button>
          )}
        </Box>
      )}
      {version.cambios && expandida && (
        <VersionCambios cambios={version.cambios} detalleDisponible={version.detalle_disponible} idPanel={idPanel} />
      )}
    </Box>
  );
};
